import { BILLING_STATUS } from '../vocabulary.js';
import {
  MAX_INVOICE_NUMBER_LENGTH,
  addMonths,
  canonicalToCents,
  daysBetween,
  formatDate,
  formatEuro,
  groupItemsByClient,
  isIsoDate,
  normalizeMoney,
  proratedAmount,
  toCents,
} from './billing.core.js';

/** @typedef {import('../../shell/types/billing.types.js').BillingItemApiResponse} BillingItem */
/** @typedef {import('../../shell/types/billing.types.js').ClientInvoiceApiRequest} ClientInvoiceApiRequest */
/** @typedef {{ month: number, day: number }} Renewal */

/**
 * @typedef {Object} ProposalLine
 * @property {BillingItem} item
 * @property {string} periodTo yyyy-MM-dd; the period starts at `item.paidUntil`
 * @property {number} amount prorated price in euros
 * @property {number|null} cost prorated cost in euros, null when the item has no cost
 * @property {boolean} bridge whether the period differs from a year
 * @property {string|null} duration e.g. `1 year + 18 days`, only for a bridge
 */

/** Items billed at most yearly move to the renewal date; longer intervals keep their own cadence. */
const MAX_ALIGNED_INTERVAL = 12;
const MONTHS_PER_YEAR = 12;
/** The invoice ends on the first renewal date at least this long after the earliest paid-until date. */
const MIN_INVOICE_MONTHS = 6;
/** 29 February is no renewal date, since most years lack it; 28 February means the end of February. */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const FEBRUARY = 2;

export const INVALID_RENEWAL = 'Enter a day of that month; 29 February is not possible.';
const monthDayFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });

const pad = (number) => String(number).padStart(2, '0');

/**
 * @param {string} monthText 1 to 12 as chosen
 * @param {string} dayText the day of the month as typed
 * @returns {Renewal|null} null unless it is a day that every year has
 */
export function parseRenewal(monthText, dayText) {
  if (!/^\d{1,2}$/.test(monthText ?? '') || !/^\d{1,2}$/.test((dayText ?? '').trim())) return null;
  const [month, day] = [Number(monthText), Number(dayText)];
  return month >= 1 && month <= 12 && day >= 1 && day <= DAYS_IN_MONTH[month - 1] ? { month, day } : null;
}

/** @param {Renewal} renewal @returns {boolean} whether it is 28 February, which stands for the end of February */
export const isEndOfFebruary = ({ month, day }) => month === FEBRUARY && day === DAYS_IN_MONTH[FEBRUARY - 1];

/**
 * @param {Renewal} renewal
 * @returns {string} e.g. `1 February`, or `28 February (end of February)`
 */
export function renewalText(renewal) {
  const text = monthDayFormat.format(Date.UTC(2001, renewal.month - 1, renewal.day));
  return isEndOfFebruary(renewal) ? `${text} (end of February)` : text;
}

/** @returns {string} the renewal date in `year`, yyyy-MM-dd; 28 February is the last day of February */
function renewalIn(renewal, year) {
  return isEndOfFebruary(renewal) ? addMonths(`${year}-01-31`, 1) : `${year}-${pad(renewal.month)}-${pad(renewal.day)}`;
}

/**
 * @param {Renewal} renewal
 * @param {string} date yyyy-MM-dd
 * @returns {string} the first renewal date on or after `date`, yyyy-MM-dd
 */
export function nextRenewal(renewal, date) {
  const year = Number(date.slice(0, 4));
  const sameYear = renewalIn(renewal, year);
  return sameYear >= date ? sameYear : renewalIn(renewal, year + 1);
}

/**
 * @param {BillingItem[]} items items of one client
 * @returns {Renewal|null} the client's stored renewal date
 */
export function storedRenewal(items) {
  const { renewalMonth, renewalDay } = items[0] ?? {};
  return renewalMonth == null ? null : { month: renewalMonth, day: renewalDay };
}

/**
 * The renewal date to propose when the client has none: the day of the paid-until date of the item with
 * the highest yearly price (the earliest paid-until date breaks a tie), 29 February moving to the 28th.
 *
 * @param {BillingItem[]} items at least one active item of one client
 * @returns {Renewal}
 */
export function suggestedRenewal(items) {
  const [top] = [...items].sort((a, b) => toCents(b.price) * a.intervalMonths - toCents(a.price) * b.intervalMonths
    || a.paidUntil.localeCompare(b.paidUntil));
  const month = Number(top.paidUntil.slice(5, 7));
  return { month, day: Math.min(Number(top.paidUntil.slice(8, 10)), DAYS_IN_MONTH[month - 1]) };
}

const plural = (count, unit) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/**
 * @param {string} from yyyy-MM-dd
 * @param {string} to yyyy-MM-dd, after `from`
 * @returns {string} whole years, months and days in between, e.g. `1 year + 18 days`
 */
export function durationText(from, to) {
  let months = 0;
  while (addMonths(from, months + 1) <= to) months++;
  const days = daysBetween(addMonths(from, months), to);
  const parts = [[Math.floor(months / 12), 'year'], [months % 12, 'month'], [days, 'day']]
    .filter(([count]) => count > 0)
    .map(([count, unit]) => plural(count, unit));
  return parts.join(' + ');
}

const DUE = [BILLING_STATUS.OVERDUE, BILLING_STATUS.DUE_SOON];

/**
 * One invoice for all active items of a client. Items billed at most yearly are on it only when one of them
 * is overdue or due soon. Then it covers until the first renewal date on or after both today and six months
 * after their earliest paid-until date; each of them runs to that date, its yearly rate prorated by day
 * against the year from its paid-until date, unless it is already paid until that date or later. Longer
 * intervals are on it only when they are due themselves, with their normal period and price.
 *
 * @param {BillingItem[]} items the client's active items
 * @param {Renewal} renewal
 * @param {string} today yyyy-MM-dd
 * @returns {{ coversUntil: string|null, lines: ProposalLine[], later: BillingItem[], totalCents: number }}
 *   `coversUntil` is null when no item billed at most yearly is on it; `later` are the items not on it
 */
export function clientInvoiceProposal(items, renewal, today) {
  const isAligned = (item) => item.intervalMonths <= MAX_ALIGNED_INTERVAL;
  const isDue = (item) => DUE.includes(item.status);
  const aligned = items.filter(isAligned);
  const end = aligned.some(isDue) ? alignedEnd(aligned, renewal, today) : null;
  const lines = [];
  const later = [];
  for (const item of items) {
    if (isAligned(item) ? end === null || item.paidUntil >= end : !isDue(item)) {
      later.push(item);
    } else if (isAligned(item)) {
      const yearEnd = addMonths(item.paidUntil, MONTHS_PER_YEAR);
      const prorate = (value) => proratedAmount(value, item.paidUntil, yearEnd, end,
        MONTHS_PER_YEAR, item.intervalMonths);
      const bridge = end !== yearEnd;
      lines.push({
        item,
        periodTo: end,
        amount: prorate(item.price),
        cost: prorate(item.cost),
        bridge,
        duration: bridge ? durationText(item.paidUntil, end) : null,
      });
    } else {
      lines.push({ item, periodTo: item.nextPeriodTo, amount: item.price, cost: item.cost, bridge: false, duration: null });
    }
  }
  return {
    coversUntil: lines.some((line) => isAligned(line.item)) ? end : null,
    lines,
    later,
    totalCents: lines.reduce((total, line) => total + toCents(line.amount), 0),
  };
}

/** @returns {string} the first renewal date on or after both today and the earliest paid-until date + 6 months */
function alignedEnd(aligned, renewal, today) {
  const minimum = addMonths(aligned.map((item) => item.paidUntil).sort()[0], MIN_INVOICE_MONTHS);
  return nextRenewal(renewal, minimum > today ? minimum : today);
}

/**
 * The To invoice blocks: one per client, with its renewal date (stored, else suggested) and proposal.
 *
 * @param {BillingItem[]} items active items of live clients
 * @param {boolean} dueOnly keeps the clients with at least one overdue or due soon item
 * @param {string} today yyyy-MM-dd
 * @returns {Array<{ slug: string, name: string, items: BillingItem[], renewal: Renewal, suggested: boolean,
 *   status: string, proposal: ReturnType<typeof clientInvoiceProposal> }>} `status` is the most urgent
 *   `BILLING_STATUS` of the client's items
 */
export function clientInvoiceGroups(items, dueOnly, today) {
  return groupItemsByClient(items)
    .map((group) => {
      const stored = storedRenewal(group.items);
      const renewal = stored ?? suggestedRenewal(group.items);
      const statuses = group.items.map((item) => item.status);
      const status = [...DUE, BILLING_STATUS.OK].find((candidate) => statuses.includes(candidate));
      return { ...group, renewal, suggested: stored === null, status, proposal: clientInvoiceProposal(group.items, renewal, today) };
    })
    .filter((group) => !dueOnly || DUE.includes(group.status));
}

/**
 * Validates the amount and cost of every included row.
 *
 * @param {Array<{ itemId: number, include: boolean, periodFrom: string, periodTo: string, amount: string, cost: string }>} rows
 * @returns {{ errors: Record<string, string>, lines: ClientInvoiceApiRequest['lines'] }} errors are keyed
 *   `amount-<itemId>`, `cost-<itemId>` and `lines` (nothing included); a line with an error holds null money
 */
export function validateInvoiceRows(rows) {
  const errors = {};
  const lines = rows.filter((row) => row.include).map((row) => {
    const amount = normalizeMoney(row.amount);
    const cost = normalizeMoney(row.cost, { required: false });
    if (amount.error) errors[`amount-${row.itemId}`] = amount.error;
    if (cost.error) errors[`cost-${row.itemId}`] = cost.error;
    return { itemId: row.itemId, periodFrom: row.periodFrom, periodTo: row.periodTo, amount: amount.value, cost: cost.value };
  });
  if (lines.length === 0) errors.lines = 'Include at least one item.';
  return { errors, lines };
}

/**
 * @param {ClientInvoiceApiRequest['lines']} lines valid lines
 * @returns {number} their total amount in integer cents
 */
export function linesTotalCents(lines) {
  return Number(lines.reduce((total, line) => total + canonicalToCents(line.amount), 0n));
}

/**
 * Validates the client invoice dialog the way the backend does and builds the request.
 *
 * @param {{ invoiceNumber?: string, invoicedOn?: string, renewal: Renewal|null, rows: Parameters<typeof validateInvoiceRows>[0] }} form
 * @returns {{ valid: boolean, errors: Record<string, string>, request: ClientInvoiceApiRequest|null }}
 */
export function validateClientInvoiceForm({ invoiceNumber, invoicedOn, renewal, rows }) {
  const { errors, lines } = validateInvoiceRows(rows);
  const number = (invoiceNumber ?? '').trim();
  if (number === '') errors.invoiceNumber = 'Enter an invoice number.';
  else if (number.length > MAX_INVOICE_NUMBER_LENGTH) errors.invoiceNumber = `Use at most ${MAX_INVOICE_NUMBER_LENGTH} characters.`;
  if (!isIsoDate(invoicedOn)) errors.invoicedOn = 'Enter a valid date.';
  if (renewal === null) errors.renewal = INVALID_RENEWAL;
  if (Object.keys(errors).length > 0) return { valid: false, errors, request: null };
  return {
    valid: true,
    errors,
    request: { invoiceNumber: number, invoicedOn, renewalMonth: renewal.month, renewalDay: renewal.day, lines },
  };
}

/**
 * The invoice lines in Dutch for the accounting tool, one per line and a total.
 *
 * @param {Array<{ label: string, periodFrom: string, periodTo: string, bridge: boolean, amount: string }>} lines
 *   `amount` is canonical money
 * @returns {string} e.g. `Domein: 14/01/2027 tot 01/02/2028 (overbrugging): € 52,47`
 */
export function invoiceLinesText(lines) {
  const rows = lines.map((line) => `${line.label}: ${formatDate(line.periodFrom)} tot ${formatDate(line.periodTo)}`
    + `${line.bridge ? ' (overbrugging)' : ''}: ${formatEuro(Number(line.amount))}`);
  return [...rows, `Totaal excl. btw: ${formatEuro(linesTotalCents(lines) / 100)}`].join('\n');
}
